import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/constants';
import AdminNav from '../../components/admin/AdminNav';

/**
 * ConversionMetrics - ROI Dashboard showing sales conversion analytics
 * 
 * Displays:
 * - Lead distribution and conversion funnel
 * - Revenue from won deals
 * - Conversion rates
 * - Lost reasons breakdown
 * - Recent wins
 */
const ConversionMetrics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState(90);

  // Track AdminNav mode for responsive layout
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  useEffect(() => {
    fetchMetrics();
  }, [period]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/conversion/metrics?days=${period}`, {
        headers: {
          'X-Local-Dev': 'true',  // Bypass auth for local development
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch conversion metrics');
      }
      
      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Calculate navigation offset based on mode
  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4';
      case 'collapsed': return 'ml-20';
      default: return 'ml-64';
    }
  };

  // Loading state
  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-[#00314F]">
        <AdminNav onModeChange={setNavMode} />
        <div className={`${getNavOffset()} transition-all duration-300 px-4 py-4 pt-20`}>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-white/20 border-t-[#0DB14B] rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-white text-lg">Loading conversion metrics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#00314F]">
      <AdminNav onModeChange={setNavMode} />
      <div className={`${getNavOffset()} transition-all duration-300 px-4 py-4 pt-20`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <span className="text-3xl">📊</span>
              Lead Conversion ROI
            </h1>
            <p className="text-white/60 mt-2">
              Track sales conversions and revenue from TagQuest leads
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center gap-2">
            <span className="text-white/60 text-sm">Period:</span>
            <select
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              className="bg-[#00314F] border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#0DB14B]"
              style={{ colorScheme: 'dark' }}
            >
              <option value={30} className="bg-[#00314F] text-white">Last 30 days</option>
              <option value={60} className="bg-[#00314F] text-white">Last 60 days</option>
              <option value={90} className="bg-[#00314F] text-white">Last 90 days</option>
              <option value={180} className="bg-[#00314F] text-white">Last 6 months</option>
              <option value={365} className="bg-[#00314F] text-white">Last year</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
            <button
              onClick={fetchMetrics}
              className="mt-2 text-sm text-red-300 underline hover:text-red-200"
            >
              Retry
            </button>
          </div>
        )}

        {metrics && (
          <>
            {/* Top Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Total Distributed */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
                <div className="text-white/50 text-sm mb-2">Leads Distributed</div>
                <div className="text-3xl font-bold text-white">
                  {metrics.summary.total_leads_distributed}
                </div>
                <div className="text-white/40 text-xs mt-2">Sent to sales team</div>
              </div>

              {/* Total Revenue */}
              <div className="bg-gradient-to-br from-[#0DB14B]/20 to-[#0DB14B]/10 border border-[#0DB14B]/30 rounded-xl p-6">
                <div className="text-[#0DB14B] text-sm mb-2">Total Revenue</div>
                <div className="text-3xl font-bold text-white">
                  {formatCurrency(metrics.summary.total_revenue)}
                </div>
                <div className="text-white/40 text-xs mt-2">From won deals</div>
              </div>

              {/* Conversion Rate */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
                <div className="text-white/50 text-sm mb-2">Conversion Rate</div>
                <div className="text-3xl font-bold text-white">
                  {formatPercent(metrics.summary.conversion_rate)}
                </div>
                <div className="text-white/40 text-xs mt-2">Won / (Won + Lost)</div>
              </div>

              {/* Avg Deal Value */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
                <div className="text-white/50 text-sm mb-2">Avg Deal Value</div>
                <div className="text-3xl font-bold text-white">
                  {formatCurrency(metrics.summary.avg_deal_value)}
                </div>
                <div className="text-white/40 text-xs mt-2">Per won deal</div>
              </div>
            </div>

            {/* Pipeline Funnel */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 mb-8">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <span>🔄</span> Sales Pipeline
              </h2>
              
              <div className="space-y-4">
                {/* Distributed */}
                <div 
                  className="flex items-center gap-4 cursor-pointer hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors"
                  onClick={() => navigate('/admin/approvals?sales_status=distributed')}
                  title="Click to view distributed leads"
                >
                  <div className="w-24 text-sm text-white/70">Distributed</div>
                  <div className="flex-1 bg-white/5 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 flex items-center justify-end px-3"
                      style={{ width: '100%' }}
                    >
                      <span className="text-white text-sm font-medium">
                        {metrics.pipeline.distributed + metrics.pipeline.new}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contacted */}
                <div 
                  className="flex items-center gap-4 cursor-pointer hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors"
                  onClick={() => navigate('/admin/approvals?sales_status=contacted')}
                  title="Click to view contacted leads"
                >
                  <div className="w-24 text-sm text-white/70">Contacted</div>
                  <div className="flex-1 bg-white/5 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500 flex items-center justify-end px-3"
                      style={{ 
                        width: `${Math.max(5, (metrics.pipeline.contacted / Math.max(1, metrics.summary.total_leads_distributed)) * 100)}%` 
                      }}
                    >
                      <span className="text-white text-sm font-medium">
                        {metrics.pipeline.contacted}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quoting */}
                <div 
                  className="flex items-center gap-4 cursor-pointer hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors"
                  onClick={() => navigate('/admin/approvals?sales_status=quoting')}
                  title="Click to view quoting leads"
                >
                  <div className="w-24 text-sm text-white/70">Quoting</div>
                  <div className="flex-1 bg-white/5 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 flex items-center justify-end px-3"
                      style={{ 
                        width: `${Math.max(5, (metrics.pipeline.quoting / Math.max(1, metrics.summary.total_leads_distributed)) * 100)}%` 
                      }}
                    >
                      <span className="text-white text-sm font-medium">
                        {metrics.pipeline.quoting}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Won */}
                <div 
                  className="flex items-center gap-4 cursor-pointer hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors"
                  onClick={() => navigate('/admin/approvals?sales_status=won')}
                  title="Click to view won leads"
                >
                  <div className="w-24 text-sm text-[#0DB14B] font-medium">Won</div>
                  <div className="flex-1 bg-white/5 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-[#0DB14B] flex items-center justify-end px-3"
                      style={{ 
                        width: `${Math.max(5, (metrics.pipeline.won / Math.max(1, metrics.summary.total_leads_distributed)) * 100)}%` 
                      }}
                    >
                      <span className="text-white text-sm font-medium">
                        {metrics.pipeline.won}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Lost */}
                <div 
                  className="flex items-center gap-4 cursor-pointer hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors"
                  onClick={() => navigate('/admin/approvals?sales_status=lost')}
                  title="Click to view lost leads"
                >
                  <div className="w-24 text-sm text-red-400">Lost</div>
                  <div className="flex-1 bg-white/5 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-red-500 flex items-center justify-end px-3"
                      style={{ 
                        width: `${Math.max(5, (metrics.pipeline.lost / Math.max(1, metrics.summary.total_leads_distributed)) * 100)}%` 
                      }}
                    >
                      <span className="text-white text-sm font-medium">
                        {metrics.pipeline.lost}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-white/40 text-xs mt-4 text-center">
                💡 Click any bar to view those leads in the approvals page
              </p>

              {/* Pipeline Value */}
              {metrics.summary.pipeline_value > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Pipeline Value (Quoting Stage)</span>
                    <span className="text-xl font-bold text-purple-400">
                      {formatCurrency(metrics.summary.pipeline_value)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Recent Wins */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>🏆</span> Recent Wins
                </h2>
                
                {metrics.recent_wins && metrics.recent_wins.length > 0 ? (
                  <div className="space-y-3">
                    {metrics.recent_wins.map((deal, idx) => (
                      <div 
                        key={deal.id || idx}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                      >
                        <div>
                          <div className="text-white font-medium">{deal.business_name}</div>
                          <div className="text-white/40 text-xs">
                            {deal.won_at ? new Date(deal.won_at).toLocaleDateString() : 'Recently'}
                          </div>
                        </div>
                        <div className="text-[#0DB14B] font-bold">
                          {formatCurrency(deal.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-white/40 text-center py-8">
                    No wins recorded yet. Sales reps can mark leads as won via email magic links.
                  </div>
                )}
              </div>

              {/* Lost Reasons */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>📉</span> Lost Reasons
                </h2>
                
                {metrics.lost_reasons && Object.keys(metrics.lost_reasons).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(metrics.lost_reasons)
                      .sort((a, b) => b[1] - a[1])
                      .map(([reason, count]) => (
                        <div 
                          key={reason}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                        >
                          <div className="text-white capitalize">
                            {reason === 'unspecified' ? 'No reason given' : reason.replace(/_/g, ' ')}
                          </div>
                          <div className="text-red-400 font-medium">{count}</div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-white/40 text-center py-8">
                    No lost deals recorded yet.
                  </div>
                )}
              </div>
            </div>

            {/* Info Banner */}
            <div className="mt-8 bg-[#235EAC]/20 border border-[#235EAC]/40 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <span className="text-2xl">💡</span>
                <div>
                  <h3 className="text-white font-bold mb-2">How It Works</h3>
                  <p className="text-white/70 text-sm">
                    Every lead email sent to sales reps includes a "Update Status" button. 
                    When they click it, they can mark the lead as Contacted, Quoting, Won, or Lost 
                    without logging in. This data automatically appears here in real-time.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConversionMetrics;

